/**
 * A seção clara, logo abaixo da hero.
 *
 * É onde o #F5F5F5 da paleta faz o trabalho dele: um bloco de papel encaixado
 * entre duas seções grafite, com o topo arredondado subindo 1rem por cima da
 * hero (o `margin-top: -1rem` do template). Depois de uma tela inteira escura,
 * ele funciona como uma virada de página.
 *
 * Três coisas acontecem aqui:
 *
 *   a frase       o que a competição é, em uma frase só
 *   os escudos    a fileira de clubes, que no template são logos de exemplo e
 *                 aqui são os times inscritos de verdade
 *   os números    o que a plataforma já tem, rolando como marcador de estádio
 *
 * A fileira de escudos é o único lugar da home onde a lista de times aparece
 * sem contexto — e é justamente por isso que ela precisa ser real. Uma faixa de
 * escudos genéricos aqui seria desmentida pela página `/times`, que fica a um
 * clique de distância.
 */

import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Contador } from './Contador';
import { useEntradaDaSecao, useDerivaDosEscudos } from './animacoes';
import { TeamCrest } from '@/components/ui';
import { LogoMPC } from '@/components/LogoMPC';
import { queries } from '@/lib/api';

/** Quantos escudos cabem na fileira antes de ela virar poluição. */
const ESCUDOS_NA_FILEIRA = 12;

export function SecaoSobre() {
  const raiz = useRef<HTMLElement>(null);
  const { data: dadosTimes } = useQuery(queries.teams());
  const { data: dadosEdicoes } = useQuery(queries.tournaments());

  const times = dadosTimes?.teams ?? [];
  const edicoes = dadosEdicoes?.tournaments ?? [];

  const emDisputa = edicoes.filter((e) =>
    ['registration', 'ready', 'group_stage', 'knockout'].includes(e.status),
  ).length;

  /**
   * Os quatro números.
   *
   * O template tem cinco (times, jogadores, torneios, partidas, parceiros).
   * "Jogadores" e "parceiros" ficaram de fora porque a plataforma não sabe
   * responder: o elenco só vem na consulta de cada time, e parceiro não existe
   * ainda. Preencher os dois com um número plausível seria o tipo de detalhe
   * que ninguém confere e que, no dia em que alguém conferir, derruba a
   * credibilidade dos outros três junto.
   */
  const numeros = [
    { valor: times.length, rotulo: 'Times', sufixo: '+' },
    { valor: edicoes.filter((e) => e.status === 'finished').length, rotulo: 'Edições concluídas' },
    { valor: emDisputa, rotulo: 'Em disputa' },
    { valor: times.filter((t) => t.eaVerifiedAt).length, rotulo: 'Clubes verificados' },
  ];

  const fileira = times.slice(0, ESCUDOS_NA_FILEIRA);
  const metade = Math.ceil(fileira.length / 2);

  useEntradaDaSecao(raiz, '[data-entra]', {
    dependencias: [times.length, edicoes.length],
  });
  useDerivaDosEscudos(raiz);

  return (
    <section id="sobre" ref={raiz} className="about-section">
      <div className="container">
        <h2 className="about-title dark-color" data-entra>
          A MPC é uma competição de Pro Clubs feita para quem joga: sorteio que qualquer um
          confere, tabela recalculada a cada resultado e estatísticas que vêm do próprio
          EA SPORTS FC.
        </h2>
      </div>

      {/* A fileira deriva devagar para o lado, como no template. O emblema da
          competição fica no meio, no lugar onde o original põe uma arte larga. */}
      <div className="about-animate-contents" data-deriva>
        {fileira.slice(0, metade).map((time) => (
          <div key={time.id} className="club-icon">
            <TeamCrest team={time} size="lg" />
          </div>
        ))}

        <div className="club-price">
          <LogoMPC altura={120} className="mx-auto" />
        </div>

        {fileira.slice(metade).map((time) => (
          <div key={time.id} className="club-icon">
            <TeamCrest team={time} size="lg" />
          </div>
        ))}
      </div>

      <div className="container">
        <div className="about-counter-block">
          {numeros.map((n, i) => (
            <Fragmento key={n.rotulo} mostrarLinha={i > 0}>
              <div className="about-counter-flex" data-entra>
                <Contador valor={n.valor} sufixo={n.valor > 0 ? n.sufixo : undefined} />
                <div className="about-counter-para">{n.rotulo}</div>
              </div>
            </Fragmento>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * A régua vertical entre dois números.
 *
 * Vive num componente porque ela só existe *entre* itens: renderizada dentro do
 * item, o primeiro da fila ganharia um traço solto na borda esquerda do bloco.
 */
function Fragmento({
  mostrarLinha,
  children,
}: {
  mostrarLinha: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      {mostrarLinha && <div className="about-counter-line" aria-hidden />}
      {children}
    </>
  );
}
